type TelegramInset = { top?: number; right?: number; bottom?: number; left?: number };

type TelegramEvent =
  | "viewportChanged"
  | "safeAreaChanged"
  | "contentSafeAreaChanged"
  | "fullscreenChanged"
  | "fullscreenFailed"
  | "themeChanged";

type TelegramWebApp = {
  version?: string;
  platform?: string;
  isFullscreen?: boolean;
  viewportStableHeight?: number;
  safeAreaInset?: TelegramInset;
  contentSafeAreaInset?: TelegramInset;
  onEvent?(event: TelegramEvent, callback: (payload?: unknown) => void): void;
  offEvent?(event: TelegramEvent, callback: (payload?: unknown) => void): void;
  ready?(): void;
  expand?(): void;
  disableVerticalSwipes?(): void;
  requestFullscreen?(): void;
  isVersionAtLeast?(version: string): boolean;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
};

type TelegramWindow = Window & {
  Telegram?: {
    WebApp?: TelegramWebApp;
    WebView?: {
      postEvent?(
        event: string,
        callback?: boolean | (() => void) | null,
        data?: Record<string, unknown>,
      ): void;
    };
  };
  TelegramWebviewProxy?: { postEvent?(event: string, data: string): void };
};

const TELEGRAM_FULLSCREEN_MIN_VERSION = "8.0";
const TELEGRAM_BOOT_RETRIES = 30;
const TELEGRAM_BOOT_RETRY_MS = 200;
const ANDROID_SAFE_BOTTOM = 24;
const APP_SURFACE = "#c9e9f4";
const APP_BOTTOM = "#247f95";

function parseVersion(version: string): number[] {
  return version.split(".").map((part) => {
    const parsed = Number.parseInt(part, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  });
}

function isVersionAtLeast(current: string, minimum: string): boolean {
  const left = parseVersion(current);
  const right = parseVersion(minimum);
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const leftPart = left[index] ?? 0;
    const rightPart = right[index] ?? 0;
    if (leftPart > rightPart) return true;
    if (leftPart < rightPart) return false;
  }
  return true;
}

function canRequestFullscreen(webApp: TelegramWebApp): boolean {
  if (typeof webApp.requestFullscreen !== "function") return false;
  if (typeof webApp.isVersionAtLeast === "function") {
    try {
      return webApp.isVersionAtLeast(TELEGRAM_FULLSCREEN_MIN_VERSION);
    } catch {
      return false;
    }
  }
  return webApp.version === undefined || isVersionAtLeast(webApp.version, TELEGRAM_FULLSCREEN_MIN_VERSION);
}

function detectPlatform(webApp: TelegramWebApp): "android" | "ios" | "other" {
  const platform = (webApp.platform ?? "").toLowerCase();
  if (platform.includes("android")) return "android";
  if (platform.includes("ios")) return "ios";
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("android")) return "android";
  if (/iphone|ipad|ipod/.test(userAgent)) return "ios";
  return "other";
}

function numberOr(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : Math.max(0, fallback);
}

function setCssVariable(name: string, value: string): void {
  const rootStyle = document.documentElement.style;
  if (rootStyle.getPropertyValue(name) !== value) rootStyle.setProperty(name, value);
}

function stableViewportHeight(webApp: TelegramWebApp, visualViewport: VisualViewport | null): number {
  const fallback = numberOr(visualViewport?.height, window.innerHeight);
  const reported = numberOr(webApp.viewportStableHeight);
  return Math.round(reported >= 320 ? reported : Math.max(320, fallback));
}

function disableVerticalSwipes(webApp: TelegramWebApp): void {
  const telegram = (window as TelegramWindow).Telegram;
  try {
    webApp.disableVerticalSwipes?.();
  } catch {
    // Older Telegram clients may expose the method before it is usable.
  }
  try {
    telegram?.WebView?.postEvent?.("web_app_setup_swipe_behavior", false, {
      allow_vertical_swipe: false,
    });
  } catch {
    // The direct bridge is optional.
  }
  try {
    (window as TelegramWindow).TelegramWebviewProxy?.postEvent?.(
      "web_app_setup_swipe_behavior",
      JSON.stringify({ allow_vertical_swipe: false }),
    );
  } catch {
    // The Android proxy is optional.
  }
}

function setTelegramColors(webApp: TelegramWebApp): void {
  try {
    webApp.setHeaderColor?.(APP_SURFACE);
  } catch {
    // Color APIs depend on the Telegram client version.
  }
  try {
    webApp.setBackgroundColor?.(APP_SURFACE);
  } catch {
    // Color APIs depend on the Telegram client version.
  }
  try {
    webApp.setBottomBarColor?.(APP_BOTTOM);
  } catch {
    // Color APIs depend on the Telegram client version.
  }
}

function fullscreenFromPayload(payload: unknown, webApp: TelegramWebApp): boolean {
  if (typeof payload === "boolean") return payload;
  if (payload !== null && typeof payload === "object") {
    const snakeCase = (payload as { is_fullscreen?: unknown }).is_fullscreen;
    const camelCase = (payload as { isFullscreen?: unknown }).isFullscreen;
    if (typeof snakeCase === "boolean") return snakeCase;
    if (typeof camelCase === "boolean") return camelCase;
  }
  return webApp.isFullscreen === true;
}

function applyViewport(webApp: TelegramWebApp, payload?: unknown): void {
  const platform = detectPlatform(webApp);
  const safe = webApp.safeAreaInset ?? {};
  const content = webApp.contentSafeAreaInset ?? {};
  const visualViewport = window.visualViewport;
  const fallbackTop = numberOr(visualViewport?.offsetTop);
  const fallbackBottom = Math.max(
    0,
    window.innerHeight - numberOr(visualViewport?.height, window.innerHeight) - fallbackTop,
  );

  const safeTop = numberOr(safe.top ?? content.top, fallbackTop);
  const safeRight = numberOr(safe.right ?? content.right);
  const rawSafeBottom = numberOr(safe.bottom ?? content.bottom, fallbackBottom);
  const safeLeft = numberOr(safe.left ?? content.left);
  const contentTop = numberOr(content.top, safeTop);
  const contentRight = numberOr(content.right, safeRight);
  const rawContentBottom = numberOr(content.bottom, rawSafeBottom);
  const contentLeft = numberOr(content.left, safeLeft);
  const hasSystemTop = safe.top !== undefined && Number.isFinite(safe.top);
  const androidSystemTop = platform === "android" && hasSystemTop ? safeTop : 0;
  const safeBottom = platform === "android" ? Math.max(rawSafeBottom, ANDROID_SAFE_BOTTOM) : rawSafeBottom;
  const contentBottom =
    platform === "android"
      ? Math.max(rawContentBottom, rawSafeBottom, ANDROID_SAFE_BOTTOM)
      : rawContentBottom;
  const stableHeight = stableViewportHeight(webApp, visualViewport);

  document.documentElement.dataset.platform = platform;
  document.documentElement.dataset.telegram = "true";
  document.documentElement.dataset.fullscreen = String(fullscreenFromPayload(payload, webApp));
  setCssVariable("--tg-safe-area-inset-top", `${safeTop}px`);
  setCssVariable("--tg-safe-area-inset-right", `${safeRight}px`);
  setCssVariable("--tg-safe-area-inset-bottom", `${safeBottom}px`);
  setCssVariable("--tg-safe-area-inset-left", `${safeLeft}px`);
  setCssVariable("--tg-content-safe-area-inset-top", `${contentTop}px`);
  setCssVariable("--tg-content-safe-area-inset-right", `${contentRight}px`);
  setCssVariable("--tg-content-safe-area-inset-bottom", `${contentBottom}px`);
  setCssVariable("--tg-content-safe-area-inset-left", `${contentLeft}px`);
  setCssVariable("--safe-top", `${contentTop}px`);
  setCssVariable("--safe-right", `${contentRight}px`);
  setCssVariable("--safe-bottom", `${contentBottom}px`);
  setCssVariable("--safe-left", `${contentLeft}px`);
  setCssVariable("--android-system-safe-top", `${androidSystemTop}px`);
  setCssVariable("--stable-height", `${stableHeight}px`);
}

export function installTelegramViewport(): () => void {
  let disposed = false;
  let retryTimer = 0;
  let attempts = 0;
  let cleanupWebApp: (() => void) | null = null;

  const install = (): void => {
    if (disposed || cleanupWebApp !== null) return;
    const webApp = (window as TelegramWindow).Telegram?.WebApp;
    if (webApp === undefined) {
      attempts += 1;
      if (attempts < TELEGRAM_BOOT_RETRIES) retryTimer = window.setTimeout(install, TELEGRAM_BOOT_RETRY_MS);
      return;
    }

    const apply = (payload?: unknown) => applyViewport(webApp, payload);
    const handleViewport = () => {
      apply();
      disableVerticalSwipes(webApp);
    };
    const handleFullscreenFailed = () => {
      apply();
      try {
        webApp.expand?.();
      } catch {
        // expand() remains the non-fullscreen fallback.
      }
    };

    try {
      webApp.ready?.();
      webApp.expand?.();
    } catch {
      // The viewport integration is best-effort and must not block the game.
    }
    disableVerticalSwipes(webApp);
    setTelegramColors(webApp);
    apply();

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", handleViewport);
    visualViewport?.addEventListener("scroll", handleViewport);
    window.addEventListener("resize", handleViewport);
    webApp.onEvent?.("viewportChanged", handleViewport);
    webApp.onEvent?.("safeAreaChanged", handleViewport);
    webApp.onEvent?.("contentSafeAreaChanged", handleViewport);
    webApp.onEvent?.("fullscreenChanged", apply);
    webApp.onEvent?.("fullscreenFailed", handleFullscreenFailed);
    webApp.onEvent?.("themeChanged", handleViewport);

    if (canRequestFullscreen(webApp) && webApp.isFullscreen !== true) {
      try {
        webApp.requestFullscreen?.();
        webApp.expand?.();
      } catch {
        // Telegram may reject fullscreen on an unsupported client; expanded mode still works.
      }
    }

    cleanupWebApp = () => {
      visualViewport?.removeEventListener("resize", handleViewport);
      visualViewport?.removeEventListener("scroll", handleViewport);
      window.removeEventListener("resize", handleViewport);
      webApp.offEvent?.("viewportChanged", handleViewport);
      webApp.offEvent?.("safeAreaChanged", handleViewport);
      webApp.offEvent?.("contentSafeAreaChanged", handleViewport);
      webApp.offEvent?.("fullscreenChanged", apply);
      webApp.offEvent?.("fullscreenFailed", handleFullscreenFailed);
      webApp.offEvent?.("themeChanged", handleViewport);
    };
  };

  install();

  return () => {
    disposed = true;
    window.clearTimeout(retryTimer);
    cleanupWebApp?.();
  };
}
